import { z } from 'zod';
import { isoDateSchema, languageSchema, mobileSchema, objectIdSchema, timezoneSchema, } from './common.js';
/**
 * The nine goals as SEED DATA, not a validation enum.
 *
 * Goals live in the database so a tenth can be added from the portal — with translated
 * names — without an app release. Anything that validates a goal checks it against the
 * goals collection, never against this list. This exists only to seed a fresh database.
 */
export const SEED_GOAL_KEYS = [
    'OVERTHINKING',
    'MANAGE_ANGER',
    'MOVE_ON_FROM_BREAKUP',
    'RELATIONSHIPS',
    'GOALS',
    'WEALTH_MINDSET',
    'CONFIDENCE',
    'SLEEP',
    'DISCIPLINE',
];
/**
 * A goal key. Shape is validated here; existence is validated against the database, which
 * is what produces a 409 rather than a 400 for a key that simply is not there.
 */
export const goalKeySchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Invalid goal');
/** Daily time preference. A soft signal for recommendation — it restricts nothing. */
export const dailyMinutesSchema = z.union([z.literal(5), z.literal(10), z.literal(15)]);
/**
 * 1-3 goals. The FIRST element is the primary goal — it drives what Home shows and
 * which journey onboarding matches. Order is meaning, not presentation.
 */
export const userGoalsSchema = z.array(goalKeySchema).min(1).max(3);
/**
 * Profile detail added for artboard 6b on 2026-09-28.
 *
 * All optional and all nullable: none of it gates anything, and a user who signed up with
 * a mobile number alone stays perfectly valid. The mobile itself is NOT here — it is the
 * identity, shown read-only on 6b and changed only by signing in again.
 *
 * DATE OF BIRTH AND GENDER ARE PERSONAL DATA. They have to be declared in both stores'
 * privacy labels and in the privacy policy, and under India's DPDP Act they need a stated
 * purpose. Nothing reads them yet — if a use does not arrive, the right move is to stop
 * collecting them rather than to keep them lying in the database.
 */
export const genderSchema = z.enum(['FEMALE', 'MALE', 'OTHER']);
/** A bare calendar date, YYYY-MM-DD. No timezone: a birthday is not an instant. */
export const dateOfBirthSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 1998-04-23');
export const userResponseSchema = z.object({
    id: objectIdSchema,
    mobile: mobileSchema,
    name: z.string().nullable(),
    timezone: timezoneSchema,
    onboardingCompleted: z.boolean(),
    goals: z.array(goalKeySchema),
    dailyMinutes: dailyMinutesSchema.nullable(),
    /** Signed per read like every other uploaded image. Null falls back to the initial. */
    photoUrl: z.string().nullable(),
    email: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    city: z.string().nullable(),
    gender: genderSchema.nullable(),
    /** Content ships multilingual; only English is authored in the MVP. */
    preferredLanguage: languageSchema.nullable(),
    createdAt: isoDateSchema,
});
export const updateProfileBodySchema = z
    .object({
    // Nullable as well as optional: 6b has to be able to CLEAR a field, and omitting it
    // would mean "leave alone" rather than "remove". `name` is nullable for the same
    // reason — a user who empties the field on 6b and saves must not get the old name
    // back. `timezone` is not: it is set by the device, never typed, and an account with
    // no timezone has no local day to run its streak on.
    name: z.string().trim().min(1).max(60).nullable().optional(),
    timezone: timezoneSchema.optional(),
    email: z
        .string()
        .trim()
        .toLowerCase()
        .email('Enter a valid email address')
        .max(160)
        .nullable()
        .optional(),
    dateOfBirth: dateOfBirthSchema.nullable().optional(),
    city: z.string().trim().min(1).max(80).nullable().optional(),
    gender: genderSchema.nullable().optional(),
    preferredLanguage: languageSchema.nullable().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });
/**
 * Both onboarding screens in one call: the goals, the daily time, and the timezone the
 * streak will run on. Sets onboardingCompleted — a user who quits at the preview screen
 * must never be shown onboarding again.
 */
export const onboardingBodySchema = z.object({
    goals: userGoalsSchema,
    dailyMinutes: dailyMinutesSchema,
    timezone: z.string().min(1).max(64).default('Asia/Kolkata'),
});
export const goalOptionSchema = z.object({
    key: z.string(),
    name: z.string(),
    iconUrl: z.string().nullable(),
    order: z.number().int(),
});
//# sourceMappingURL=user.js.map